import { useClassPerfTrends } from '../../../api/hooks';
import { PanelCard, StatTile, StatGrid } from '../../../components/ui';
import ClassTrendChart from '../../../components/charts/ClassTrendChart';
import Spinner from '../../../components/ui/Spinner';
import ErrorPanel from '../../../components/ui/ErrorPanel';
import EmptyState from '../../../components/ui/EmptyState';
import { FaChartLine } from '../../../components/ui/icons';
import { Key, Legend, signed1 } from '../lib/cpBits';
import { PASS_MARK } from '../../../lib/tableRules';

interface Props {
  gradeId: number;
  sectionId: number | 'all';
  subjectId: number | 'all';
  weeks?: number;
  /** "Grade 10 · all sections", for the hint under the title. */
  ctx?: string;
}

const fmtPct = (v: number | null) => v == null ? '—' : `${v.toFixed(1)}%`;

export default function TrendsTab({ gradeId, sectionId, subjectId, weeks, ctx }: Props) {
  const q = useClassPerfTrends({
    grade: gradeId, section: sectionId, subject: subjectId, weeks,
  });
  if (q.isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (q.isError || !q.data) return <ErrorPanel />;
  const d = q.data;
  const scores = d.series.map((s) => s.avg_score).filter((v): v is number => v != null);
  const start = scores.length > 0 ? scores[0] : null;
  const latest = scores.length > 0 ? scores[scores.length - 1] : null;
  const change = start != null && latest != null ? latest - start : null;
  const best = scores.length > 0 ? Math.max(...scores) : null;
  const hint = [ctx, `last ${weeks ?? 12} weeks`, 'change the window above'].filter(Boolean).join(' · ');
  return (
    <PanelCard title={`Weekly average · ${d.subject_name}`} help="weekly_trend" align="end" subtitle={hint}
      actions={(
        <Legend>
          <Key tone="slate">Weekly average</Key>
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className="w-3.5 border-t-2 border-dashed border-accent-ink" />Pass mark
          </span>
        </Legend>
      )}>
      {d.series.length === 0 ? (
        <EmptyState
          icon={<FaChartLine />}
          title="No trend data yet"
          body="Check back once data rolls in."
        />
      ) : (
        <>
          <ClassTrendChart
            passMark={PASS_MARK}
            ariaLabel={start != null && latest != null
              ? `Weekly average for ${d.subject_name} moved from ${start.toFixed(1)}% to ${latest.toFixed(1)}% over ${d.series.length} weeks`
              : 'Weekly average score trend'}
            data={d.series.map((s) => ({ week_start: s.week_start, avg: s.avg_score }))}
          />
          <StatGrid className="mt-[18px]">
            <StatTile label="Start of window" value={fmtPct(start)} />
            <StatTile label="Latest week" value={fmtPct(latest)} tone="navy" />
            <StatTile label="Change"
              value={change == null ? '—' : <span className={change > 0 ? 'text-success' : change < 0 ? 'text-danger-ink' : ''}>{`${signed1(change)} pts`}</span>} />
            <StatTile label="Best week" value={fmtPct(best)} />
          </StatGrid>
        </>
      )}
    </PanelCard>
  );
}
